import { useTranslation } from 'react-i18next';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'components/ui/accordion';
import ReportHealthReading from 'components/reports/ReportHealthReading';
import { getTitleComment } from 'lib/utils';
import { ReportQuestionnaire, CategorizedReadingData } from '../../types';
import { ScrollArea } from 'components/ui/scroll-area';
import PDFPreview from 'components/PDFPreview';
import useAuthStore from 'store/auth';

interface InputDataProps {
  scanData: CategorizedReadingData;
  questionnaireData: ReportQuestionnaire[];
  labReportLink: string;
}

export interface DropdownOption {
  label: string;
  isSelected: boolean;
  choices: string | string[];
  selectedChoices: string | string[];
}

const InputQuestion = ({
  question,
  index,
  locale,
}: {
  question: ReportQuestionnaire;
  index: number;
  locale: string;
}) => {
  const langQuestion =
    locale === 'es' ? question.spanish_question : question.eng_question;
  const { title, comment } = getTitleComment(langQuestion);

  const answer =
    locale === 'es' ? question.spanish_choices : question.eng_choices;

  return (
    <li className='text-base font-medium text-primary'>
      <div>
        <div className='font-semibold leading-tight'>
          {index + 1}. {title}
        </div>

        {comment ? <p className='leading-tight'>({comment})</p> : null}
      </div>

      <div className='grid mt-3 gap-x-2 grid-cols-[10px_1fr]'>
        <div className='w-1.5 h-full rounded-2xl bg-primary block' />

        <p className='leading-tight'>{answer || 'N/A'}</p>
      </div>
    </li>
  );
};

export default function InputData({
  scanData,
  questionnaireData,
  labReportLink,
}: InputDataProps) {
  const { t } = useTranslation();
  const locale = useAuthStore((store) => store.locale);

  return (
    <div className='px-2 sm:p-4'>
      <ScrollArea className='h-fit lg:h-[calc(100vh-14.25rem)] xl:h-[calc(100vh-13.5rem)]'>
        <Accordion type='single' collapsible>
          <AccordionItem value='questionnaire'>
            <AccordionTrigger className='py-2'>
              {t('input_questionnaire_details')}
            </AccordionTrigger>
            <AccordionContent>
              <ul className='px-4 py-4 space-y-4 lg:px-10'>
                {questionnaireData?.map((question, index) => {
                  return (
                    <InputQuestion
                      key={question.q_id}
                      question={question}
                      index={index}
                      locale={locale}
                    />
                  );
                })}
              </ul>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value='scan-details'>
            <AccordionTrigger className='py-2'>
              {t('input_scan_details')}
            </AccordionTrigger>
            <AccordionContent>
              <ReportHealthReading data={scanData} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value='lab-report'>
            <AccordionTrigger className='py-2'>
              {t('lab_report')}
            </AccordionTrigger>
            <AccordionContent>
              <PDFPreview url={labReportLink} />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </ScrollArea>
    </div>
  );
}
